/**
 * Proctoring Config Store - Adjustable thresholds for the proctoring system
 *
 * This store holds all configurable values for proctoring:
 * - Timing (verification interval, warning delays)
 * - Thresholds (similarity threshold, max failures)
 * - Scoring weights (identity, presence, behavior)
 * - Violation penalties (per-event point deductions)
 *
 * Values persist to localStorage so you can tune them in the test page
 * and have them apply to the real assessment.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AssessmentProctoringConfig } from '@really-assess/shared/assessments'
import type { PublicAssessment } from '@really-assess/shared/assessments'
import { DEFAULT_TIME_LIMIT_CONFIG } from '@really-assess/shared/proctoring'
import type { AssessmentTimeLimitConfig } from '@really-assess/shared/proctoring'
import { useAssessmentDefinitionStore } from './assessmentDefinitionStore'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ScoringWeights {
  identity: number   // Weight for identity match (0-1, default 0.70)
  presence: number   // Weight for face presence (0-1, default 0.15)
  behavior: number   // Weight for behavioral score (0-1, default 0.15)
}

export interface ViolationPenalties {
  noFace: number           // Points deducted per no-face event
  multipleFaces: number    // Points deducted per multiple-faces event
  identityMismatch: number // Points deducted per identity mismatch
  tabSwitch: number        // Points deducted per tab switch
  windowBlur: number       // Points deducted per window blur
  fullscreenExit: number   // Points deducted per fullscreen exit
}

export interface ProctoringConfig {
  // Timing
  verificationIntervalMs: number      // How often to verify (ms)
  noFaceWarningThresholdMs: number    // Delay before warning on no face (ms)

  // Thresholds
  identitySimilarityThreshold: number // Min similarity to pass (0-100)
  maxConsecutiveIdentityFailures: number // Strikes before suspension

  // Scoring
  scoringWeights: ScoringWeights
  violationPenalties: ViolationPenalties

  // Pass threshold
  passingScore: number // Minimum score to pass assessment (0-100)

  // External assessment integration
  externalAssessmentUrl: string
  completionWebhookUrl: string
  completionWebhookSecret: string
  completionPollIntervalMs: number
  timeLimit: AssessmentTimeLimitConfig

  // Require fullscreen + penalise tab/window switches (per assessment, default on)
  browserLockdown: boolean
}

interface ProctoringConfigState extends ProctoringConfig {
  // Actions
  setVerificationInterval: (ms: number) => void
  setNoFaceWarningThreshold: (ms: number) => void
  setIdentitySimilarityThreshold: (threshold: number) => void
  setMaxConsecutiveFailures: (max: number) => void
  setScoringWeight: (key: keyof ScoringWeights, value: number) => void
  setViolationPenalty: (key: keyof ViolationPenalties, value: number) => void
  setPassingScore: (score: number) => void
  setExternalAssessmentUrl: (url: string) => void
  setCompletionWebhookUrl: (url: string) => void
  setCompletionWebhookSecret: (secret: string) => void
  setCompletionPollInterval: (ms: number) => void
  setAssessmentProctoringConfig: (config: AssessmentProctoringConfig) => void
  resetToDefaults: () => void
  exportConfig: () => ProctoringConfig
}

// ─────────────────────────────────────────────────────────────────────────────
// Default Values
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_PROCTORING_CONFIG: ProctoringConfig = {
  // Timing
  verificationIntervalMs: 2000,
  noFaceWarningThresholdMs: 2000,

  // Thresholds
  // Higher threshold for identity matching (40%), but identity has lower weight in final score
  // This means we check identity more carefully, but it doesn't dominate the final score
  identitySimilarityThreshold: 40,
  maxConsecutiveIdentityFailures: 999,

  // Scoring weights (must sum to 1.0)
  // Identity weight reduced to be lenient on identity verification
  // Presence and behavior are more important for proctoring integrity
  scoringWeights: {
    identity: 0.30,    // Reduced from 0.70 - lenient on identity
    presence: 0.35,    // Increased - face visibility is important
    behavior: 0.35,    // Increased - behavioral compliance is important
  },

  // Violation penalties (extremely lenient for showcase)
  violationPenalties: {
    noFace: 2,
    multipleFaces: 5,
    identityMismatch: 2,
    tabSwitch: 3,
    windowBlur: 1,
    fullscreenExit: 3,
  },

  // Pass threshold (lowered for showcase)
  passingScore: 50,

  // ReallyAvatar integration (configure at /proctoring-test or via VITE_* env at build)
  externalAssessmentUrl: '',
  completionWebhookUrl: '',
  completionWebhookSecret: '',
  completionPollIntervalMs: 3000,
  timeLimit: { ...DEFAULT_TIME_LIMIT_CONFIG },
  browserLockdown: true,
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Implementation
// ─────────────────────────────────────────────────────────────────────────────

export const useProctoringConfigStore = create<ProctoringConfigState>()(
  persist(
    (set, get) => ({
      // Initial values from defaults
      ...DEFAULT_PROCTORING_CONFIG,

      // ─────────────────────────────────────────────────────────────────
      // Timing Setters
      // ─────────────────────────────────────────────────────────────────

      setVerificationInterval: (ms) => {
        set({ verificationIntervalMs: Math.max(500, Math.min(10000, ms)) })
      },

      setNoFaceWarningThreshold: (ms) => {
        set({ noFaceWarningThresholdMs: Math.max(500, Math.min(10000, ms)) })
      },

      // ─────────────────────────────────────────────────────────────────
      // Threshold Setters
      // ─────────────────────────────────────────────────────────────────

      setIdentitySimilarityThreshold: (threshold) => {
        set({ identitySimilarityThreshold: Math.max(0, Math.min(100, threshold)) })
      },

      setMaxConsecutiveFailures: (max) => {
        set({ maxConsecutiveIdentityFailures: Math.max(1, Math.min(20, max)) })
      },

      // ─────────────────────────────────────────────────────────────────
      // Scoring Setters
      // ─────────────────────────────────────────────────────────────────

      setScoringWeight: (key, value) => {
        const clamped = Math.max(0, Math.min(1, value))
        set((state) => ({
          scoringWeights: {
            ...state.scoringWeights,
            [key]: clamped,
          },
        }))
      },

      setViolationPenalty: (key, value) => {
        const clamped = Math.max(0, Math.min(10, value))
        set((state) => ({
          violationPenalties: {
            ...state.violationPenalties,
            [key]: clamped,
          },
        }))
      },

      setPassingScore: (score) => {
        set({ passingScore: Math.max(0, Math.min(100, score)) })
      },

      setExternalAssessmentUrl: (url) => {
        set({ externalAssessmentUrl: url })
      },

      setCompletionWebhookUrl: (url) => {
        set({ completionWebhookUrl: url })
      },

      setCompletionWebhookSecret: (secret) => {
        set({ completionWebhookSecret: secret })
      },

      setCompletionPollInterval: (ms) => {
        set({ completionPollIntervalMs: Math.max(1000, Math.min(30000, ms)) })
      },

      setAssessmentProctoringConfig: (config) => {
        set({
          verificationIntervalMs: config.verificationIntervalMs,
          noFaceWarningThresholdMs: config.noFaceWarningThresholdMs,
          identitySimilarityThreshold: config.identitySimilarityThreshold,
          maxConsecutiveIdentityFailures: config.maxConsecutiveIdentityFailures,
          scoringWeights: { ...config.scoringWeights },
          violationPenalties: { ...config.violationPenalties },
          passingScore: config.passingScore,
          completionPollIntervalMs: config.completionPollIntervalMs,
          timeLimit: { ...DEFAULT_TIME_LIMIT_CONFIG, ...config.timeLimit },
          browserLockdown: config.browserLockdown ?? true,
        })
      },

      resetToDefaults: () => {
        set(DEFAULT_PROCTORING_CONFIG)
      },

      exportConfig: () => {
        const state = get()
        return {
          verificationIntervalMs: state.verificationIntervalMs,
          noFaceWarningThresholdMs: state.noFaceWarningThresholdMs,
          identitySimilarityThreshold: state.identitySimilarityThreshold,
          maxConsecutiveIdentityFailures: state.maxConsecutiveIdentityFailures,
          scoringWeights: { ...state.scoringWeights },
          violationPenalties: { ...state.violationPenalties },
          passingScore: state.passingScore,
          externalAssessmentUrl: state.externalAssessmentUrl,
          completionWebhookUrl: state.completionWebhookUrl,
          completionWebhookSecret: state.completionWebhookSecret,
          completionPollIntervalMs: state.completionPollIntervalMs,
          timeLimit: { ...state.timeLimit },
          browserLockdown: state.browserLockdown,
        }
      },
    }),
    {
      name: 'proctoring-config',
      version: 3,
      migrate: (persisted: unknown, version: number) => {
        const state = persisted as ProctoringConfig
        if (version < 1 && state.externalAssessmentUrl === 'https://example.com/assessment') {
          state.externalAssessmentUrl = ''
        }
        if (!state.timeLimit) {
          state.timeLimit = { ...DEFAULT_TIME_LIMIT_CONFIG }
        }
        if (typeof state.browserLockdown !== 'boolean') {
          state.browserLockdown = true
        }
        return state
      },
    }
  )
)

// ─────────────────────────────────────────────────────────────────────────────
// Helper to get config outside React components
// ─────────────────────────────────────────────────────────────────────────────

export function mergeAssessmentProctoring(
  assessment?: PublicAssessment | null
): ProctoringConfig {
  const state = useProctoringConfigStore.getState().exportConfig()
  const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env
  const override = assessment?.proctoringConfig as Partial<AssessmentProctoringConfig> | undefined

  const merged: ProctoringConfig = {
    ...DEFAULT_PROCTORING_CONFIG,
    ...state,
    ...override,
    scoringWeights: {
      ...DEFAULT_PROCTORING_CONFIG.scoringWeights,
      ...state.scoringWeights,
      ...override?.scoringWeights,
    },
    violationPenalties: {
      ...DEFAULT_PROCTORING_CONFIG.violationPenalties,
      ...state.violationPenalties,
      ...override?.violationPenalties,
    },
    externalAssessmentUrl: assessment?.reallyAvatarUrl || env?.VITE_REALLY_AVATAR_ASSESSMENT_URL?.trim() || state.externalAssessmentUrl,
    completionWebhookUrl: env?.VITE_REALLY_AVATAR_WEBHOOK_URL?.trim() || state.completionWebhookUrl,
    completionWebhookSecret: state.completionWebhookSecret,
    completionPollIntervalMs: override?.completionPollIntervalMs ?? state.completionPollIntervalMs,
    passingScore: override?.passingScore ?? state.passingScore,
    verificationIntervalMs: override?.verificationIntervalMs ?? state.verificationIntervalMs,
    noFaceWarningThresholdMs: override?.noFaceWarningThresholdMs ?? state.noFaceWarningThresholdMs,
    identitySimilarityThreshold: override?.identitySimilarityThreshold ?? state.identitySimilarityThreshold,
    maxConsecutiveIdentityFailures: override?.maxConsecutiveIdentityFailures ?? state.maxConsecutiveIdentityFailures,
    timeLimit: {
      ...DEFAULT_TIME_LIMIT_CONFIG,
      ...override?.timeLimit,
    },
    browserLockdown: override?.browserLockdown ?? state.browserLockdown ?? true,
  }

  return merged
}

export function getProctoringConfig(): ProctoringConfig {
  const assessment = useAssessmentDefinitionStore.getState().definition
  return mergeAssessmentProctoring(assessment)
}
